/*
=============================================
   TRAINING MATERIALS (student view)
=============================================
Two states behind one hash route, exactly like the class list and class detail
pair: `#training` is the card grid, `#training/<slug>` is one area's page. The
router in scripts/web-app.js splits on the slash and hands the tail to show(),
so there is no second data-view and no second menu entry to keep in step.

⚠️ WALLED SINCE 2026-10-08. This used to be static content with no
authorization check, identical for every student. It is no longer: only Skill
Guides is public, and every other area is loaded per user from its own chunk,
for the areas GET /me/content-access grants (audiences: arizona.edu, ConCats,
later other schools). The worker enforces it; this view only decides which
tiles to draw. Design: CONTENT-ACCESS-AUDIENCES-DESIGN-2026-10-08.md.

The public areas paint at once, before the access call returns, so the tab is
never blank on a slow connection; the walled areas join the grid when they load.
*/

import { PUBLIC_AREAS, loadTrainingAreas, hasHiddenAreas } from "../scripts/training-content.js";
import { getContentAccess } from "../scripts/content-access.js";
import { renderGrid, renderArea, findArea, wireFilter } from "../scripts/training-render.js";

const MORE_NOTE =
   "Some training areas are open only to students at schools and on teams that have been given access. " +
   "If you think you should see more here, ask your instructor.";

class AppTraining extends HTMLElement {
   connectedCallback() {
      // The router calls show() on every route change, but not before the first
      // one lands - so render here too, or the tab is blank until the user
      // navigates away and back.
      this.paint();
   }

   /* Called by initRouter() with whatever followed the slash in the hash. */
   show(param) {
      this.slug = param || null;
      this.paint();
   }

   async paint() {
      const token = (this._token = (this._token || 0) + 1);

      // First pass: what we can show with no network at all.
      if (!this.areas) this.draw(PUBLIC_AREAS, null, true);
      else this.draw(this.areas, this.access, false);

      if (!this.areas) {
         const access = await getContentAccess();
         const areas = await loadTrainingAreas(access);
         // Only cache a real answer, so a blip retries on the next visit.
         if (access.ok) { this.access = access; this.areas = areas; }
         if (token !== this._token) return; // a newer route change already painted
         this.draw(areas, access, false);
      }
   }

   draw(areas, access, pending) {
      const area = this.slug ? findArea(areas, this.slug) : null;

      if (this.slug && !area) {
         // Not loaded YET is not the same as not allowed: wait for the access
         // answer before giving up on a deep link to a walled area.
         if (pending) {
            this.innerHTML = `<div class="tr-root"><p class="tr-lede">Loading training materials...</p></div>`;
            return;
         }
         // A stale or hand-typed slug, or an area this user is not granted.
         // Send them to the grid rather than showing an empty page.
         location.replace("#training");
         return;
      }

      const note = !pending && hasHiddenAreas(access) ? MORE_NOTE : "";
      this.innerHTML = area
         ? renderArea(area, "#training")
         : renderGrid(areas, (a) => `#training/${a.slug}`, { note });

      // The Skill Guides index has a filter box. wireFilter is a no-op on any
      // page without one, so it is called unconditionally rather than guarded on
      // a slug that would then have to be kept in sync with the content file.
      wireFilter(this);

      // The area page can be long, and arriving at it half way down because the
      // grid was scrolled is disorienting.
      const scroller = this.closest("app-content");
      if (scroller) scroller.scrollTop = 0;
   }
}

customElements.define("app-training", AppTraining);
